import React, { useState, useEffect } from "react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserBiographyServices } from "../services/BiographyServices";

const SkeletonLine = ({ className = "" }) => (
    <div className={`animate-pulse bg-black/5 rounded ${className}`} />
);

const SkeletonBlock = ({ className = "" }) => (
    <div className={`animate-pulse bg-black/5 rounded-2xl ${className}`} />
);

export default function Biography() {
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await UserBiographyServices.getSettings();
                if (response.status === "success") {
                    setSettings(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch biography settings:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const renderSentences = (text, fallback) => {
        if (!text || !text.trim()) {
            return <p className="italic text-black/40">{fallback}</p>;
        }

        // Split by period, map to trim, filter out empty strings
        const sentences = text.split('.').map(s => s.trim()).filter(s => s.length > 0);

        return sentences.map((sentence, index) => (
            <p key={index}>{sentence}.</p>
        ));
    };

    return (
        <div className="min-h-screen bg-white">
            <SEO title="Biography & Artistic Journey" description="Discover the life, artistic vision, and musical journey of Temidayo Balogun." url="/biography" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1000px] mx-auto px-4 sm:px-6">
                    {/* Header Section */}
                    <div className="mb-16 sm:mb-24 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal text-black mb-6"
                            style={{
                                fontFamily: "Playfair Display, Georgia, serif",
                            }}
                        >
                            Biography
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                        {/* Image Column */}
                        <div className="lg:col-span-5">
                            <div className="lg:sticky lg:top-32">
                                <div className="relative w-full aspect-[3/4] max-w-[400px] mx-auto lg:mx-0">
                                    {/* Decorative Back Image */}
                                    <div
                                        className="absolute top-4 left-4 w-full h-full border-2 border-jazz-brown/30 bg-jazz-brown/5 transform -rotate-3 transition-transform duration-500 hover:rotate-0"
                                    />

                                    {/* Main Image */}
                                    <div className="relative w-full h-full bg-gray-100 overflow-hidden shadow-xl transform rotate-2 transition-transform duration-500 hover:rotate-0">
                                        {isLoading ? (
                                            <SkeletonBlock className="w-full h-full" />
                                        ) : (
                                            <img
                                                src={settings?.bio_image_path || "/static/images/jazz_journey_2.JPG"}
                                                alt="Temidayo Balogun Portrait"
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Quote */}
                                <div className="mt-12 hidden lg:block">
                                    {isLoading ? (
                                        <div className="space-y-3 border-l-4 border-jazz-brown/20 pl-6">
                                            <SkeletonLine className="h-4 w-full" />
                                            <SkeletonLine className="h-4 w-5/6" />
                                            <SkeletonLine className="h-4 w-4/6" />
                                        </div>
                                    ) : (
                                        <blockquote
                                            className="text-xl italic text-jazz-brown leading-relaxed border-l-4 border-jazz-brown pl-6"
                                            style={{ fontFamily: "Playfair Display, serif" }}
                                        >
                                            "{settings?.bio_quote || "Jazz is not just music, it's a conversation between the soul and the universe."}"
                                        </blockquote>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Content Column */}
                        <div className="lg:col-span-7 space-y-8">
                            <div
                                className="space-y-5 text-lg text-black leading-relaxed"
                                style={{
                                    fontFamily: "Crimson Pro, Georgia, serif",
                                }}
                            >
                                <h3
                                    className="text-2xl text-black font-medium mb-4"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    The Early Years
                                </h3>
                                {isLoading ? (
                                    <div className="space-y-4">
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-3/4" />
                                    </div>
                                ) : (
                                    renderSentences(settings?.early_years, "My journey began with a single note... more stories coming soon.")
                                )}

                                <h3
                                    className="text-2xl text-black font-medium mb-4 mt-12"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    Education & Mentorship
                                </h3>
                                {isLoading ? (
                                    <div className="space-y-4">
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-2/3" />
                                    </div>
                                ) : (
                                    renderSentences(settings?.education_mentorship, "Learning never stops. Stay tuned for academic milestones.")
                                )}

                                <h3
                                    className="text-2xl text-black font-medium mb-4 mt-12"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    Professional Career
                                </h3>
                                {isLoading ? (
                                    <div className="space-y-4">
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-5/6" />
                                    </div>
                                ) : (
                                    renderSentences(settings?.professional_career, "The stage is set for a lifetime of music. Career chronicles arriving shortly.")
                                )}

                                <div className="my-12 p-8 bg-jazz-brown/5 border-l-2 border-jazz-brown">
                                    {isLoading ? (
                                        <div className="space-y-3">
                                            <SkeletonLine className="h-4 w-full" />
                                            <SkeletonLine className="h-4 w-1/2 mt-4" />
                                        </div>
                                    ) : (
                                        <>
                                            <p className="italic text-black/90">
                                                "{settings?.goal || "My goal is to create music that transcends borders, speaking a universal language that resonates with the human spirit."}"
                                            </p>
                                        </>
                                    )}
                                </div>

                                <h3
                                    className="text-2xl text-black font-medium mb-4"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    Current Projects
                                </h3>
                                {isLoading ? (
                                    <div className="space-y-4">
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-4/5" />
                                    </div>
                                ) : (
                                    renderSentences(settings?.current_projects, "Innovative sounds are in the works. New projects dropping soon.")
                                )}
                            </div>

                            {/* Signature/Closing */}
                            <div className="pt-12 mt-12 border-t border-black/10">
                                <p
                                    className="text-3xl text-jazz-brown"
                                    style={{ fontFamily: "Mrs Saint Delafield, cursive" }}
                                >
                                    Temidayo Balogun
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
